import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { FileText, Loader2, Download, Plus, CheckCircle2, RefreshCw, FileSpreadsheet, Trash2, Send, Receipt } from "lucide-react";
import { DocumentUploader } from "./DocumentUploader";

const fmtEUR = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const STATO_STYLE = {
  Emessa: "bg-amber-100 text-amber-800 border-amber-300",
  Pagata: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export function CrediusRicorrenze() {
  const navigate = useNavigate();
  const [ricorrenze, setRicorrenze] = useState(null);
  const [feePending, setFeePending] = useState([]);
  const [avvocati, setAvvocati] = useState([]);
  const [avvFilter, setAvvFilter] = useState("");
  const [showNuova, setShowNuova] = useState(false);
  const [selForm, setSelForm] = useState({ avvocato: "", ids: [], note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/credius/meta").then((r) => setAvvocati(r.data.avvocati || [])).catch(() => {});
    api.get("/credius/ricorrenze" + (avvFilter ? `?avvocato_id=${avvFilter}` : ""))
      .then((r) => setRicorrenze(r.data)).catch(() => setRicorrenze([]));
    api.get("/credius/ricorrenze/fee-pending").then((r) => setFeePending(r.data)).catch(() => setFeePending([]));
  };
  useEffect(load, [avvFilter]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  const feeByAvv = feePending.reduce((acc, f) => {
    if (!acc[f.avvocato_fornitore_id]) acc[f.avvocato_fornitore_id] = { nome: f.avvocato_nome, items: [] };
    acc[f.avvocato_fornitore_id].items.push(f);
    return acc;
  }, {});

  const emettiRicorrenza = async () => {
    if (!selForm.avvocato || selForm.ids.length === 0) {
      toast.error("Seleziona un avvocato e almeno una fee pending");
      return;
    }
    await run(() => api.post("/credius/ricorrenze", {
      avvocato_fornitore_id: selForm.avvocato,
      fee_pending_ids: selForm.ids,
      note: selForm.note,
    }).then(() => { setShowNuova(false); setSelForm({ avvocato: "", ids: [], note: "" }); }),
    "Ricorrenza emessa e trasmessa all'avvocato");
  };

  const confermaPagamento = async (r) => {
    if (!window.confirm(`Confermare il pagamento della ricorrenza ${r.codice_documento} (${fmtEUR(r.imponibile_totale)})?`)) return;
    await run(() => api.post(`/credius/ricorrenze/${r.id}/pagata`, {}), "Pagamento confermato");
  };

  // Iter123k175 · Invia la ricorrenza all'avvocato con opzione documenti
  const inviaRicorrenza = async (r, scelta) => {
    const body = {
      include_fee: scelta === "fee" || scelta === "entrambe",
      include_proforma: scelta === "proforma" || scelta === "entrambe",
    };
    const etichetta = scelta === "entrambe" ? "PDF Fee + Nota Pro-forma"
      : scelta === "proforma" ? "Nota Pro-forma" : "PDF Fee";
    if (!window.confirm(`Inviare all'avvocato ${r.avvocato_nome || ""} la ricorrenza ${r.codice_documento} con allegato: ${etichetta}?`)) return;
    await run(() => api.post(`/credius/ricorrenze/${r.id}/invia`, body),
              `Email inviata (${etichetta})`);
  };

  // Iter123k177 · Ripara fee pending orfane + Iter123k178 ricalcola base con spese generali
  const riparaFeePending = async () => {
    if (!window.confirm(
      "• Rilascia le fee bloccate in ricorrenze eliminate (non pagate), riportandole in 'Pending'.\n"
      + "• Ricalcola la base di tutte le fee Pending includendo le spese generali (D.M. 55/2014).\n\nContinuare?"
    )) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/credius/ricorrenze/ripara-fee-pending`, {});
      const parts = [
        `Fee rilasciate: ${res.fee_pending_rilasciate}`,
        `Fee pulite (notule/pratiche eliminate): ${res.fee_pending_pulite || 0}`,
        `Fee ricalcolate: ${res.fee_ricalcolate}`,
      ];
      if (res.delta_totale_fee) {
        parts.push(`Δ fee: € ${Number(res.delta_totale_fee).toFixed(2)}`);
      }
      const msg = parts.join(" · ");
      if (res.fee_pending_rilasciate > 0 || res.fee_ricalcolate > 0) toast.success(msg);
      else toast.info(msg + " (nessuna modifica necessaria)");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Operazione fallita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="credius-ricorrenze" className="space-y-4">
      <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-3 flex items-start gap-2">
        <RefreshCw className="w-5 h-5 text-indigo-800 shrink-0 mt-0.5" />
        <div className="text-sm text-indigo-900 flex-1">
          <b>Ricorrenze</b>: qui confluiscono le fee dovute allo Studio calcolate automaticamente ad ogni notula
          pagata all'avvocato. Puoi emettere una ricorrenza per singola pratica o cumulativa su più pratiche
          dello stesso avvocato — verrà generato un PDF pro-forma trasmesso all'avvocato via email.
        </div>
        {/* Iter123k177 · Recovery fee pending bloccate in ricorrenze eliminate */}
        <Button variant="outline" size="sm" data-testid="ricorrenze-ripara-fee" disabled={busy}
          onClick={riparaFeePending}
          title="Sblocca fee bloccate in ricorrenze eliminate e ricalcola la base includendo le spese generali"
          className="h-9 shrink-0 border-amber-300 text-amber-800 hover:bg-amber-50">
          <RefreshCw className="w-4 h-4 mr-1" /> Ripara & Ricalcola
        </Button>
      </div>

      {/* Fee pending disponibili */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-bold text-slate-800">Fee in attesa di consolidamento ({feePending.length})</h3>
          <Button size="sm" data-testid="ricorrenza-nuova-btn" onClick={() => setShowNuova(!showNuova)}
            variant="outline" className="ml-auto border-indigo-300 text-indigo-800 hover:bg-indigo-50">
            <Plus className="w-4 h-4 mr-1" /> Emetti ricorrenza
          </Button>
        </div>
        {showNuova && (
          <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-4 mb-3 space-y-3">
            <div>
              <Label>Avvocato *</Label>
              <select data-testid="ricorrenza-avvocato" value={selForm.avvocato}
                onChange={(e) => setSelForm({ ...selForm, avvocato: e.target.value, ids: [] })}
                className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1">
                <option value="">Seleziona un avvocato con fee pending</option>
                {Object.entries(feeByAvv).map(([aid, a]) => (
                  <option key={aid} value={aid}>{a.nome} ({a.items.length} fee)</option>
                ))}
              </select>
            </div>
            {selForm.avvocato && feeByAvv[selForm.avvocato] && (
              <div>
                <Label>Fee da includere</Label>
                <div className="space-y-1 mt-1 max-h-64 overflow-y-auto border-2 border-slate-200 rounded-lg p-2">
                  {feeByAvv[selForm.avvocato].items.map((f) => (
                    <label key={f.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded">
                      <Checkbox data-testid={`ricorrenza-check-${f.id}`}
                        checked={selForm.ids.includes(f.id)}
                        onCheckedChange={(v) => setSelForm({ ...selForm,
                          ids: v ? [...selForm.ids, f.id] : selForm.ids.filter((x) => x !== f.id) })} />
                      <span className="font-mono text-xs text-slate-500">{f.notula_codice}</span>
                      <span className="text-slate-700">{f.pratica_numero}</span>
                      <span className="text-xs text-slate-500">{f.pct_fee}% di {fmtEUR(f.base_compenso)}</span>
                      <span className="ml-auto font-bold text-indigo-900">{fmtEUR(f.fee_importo)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea rows={2} value={selForm.note} onChange={(e) => setSelForm({ ...selForm, note: e.target.value })} className="mt-1" />
            </div>
            <div className="actions-row flex flex-wrap gap-2">
              <Button data-testid="ricorrenza-emetti" onClick={emettiRicorrenza} disabled={busy || selForm.ids.length === 0}
                className="bg-indigo-900 hover:bg-indigo-950">
                Emetti ricorrenza ({selForm.ids.length} pratiche)
              </Button>
              <Button variant="outline" onClick={() => setShowNuova(false)}>Annulla</Button>
            </div>
          </div>
        )}
      </div>

      {/* Filtro avvocato per elenco ricorrenze */}
      <div className="flex items-center gap-3">
        <h3 className="font-bold text-slate-800">Ricorrenze emesse</h3>
        <Button size="sm" variant="outline" data-testid="ricorrenze-export-excel"
          onClick={() => {
            const p = avvFilter ? `?avvocato_id=${avvFilter}` : "";
            openSecureFile(`/credius/export/ricorrenze-excel${p}`);
          }}
          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Export Excel
        </Button>
        <div className="ml-auto sm:w-64">
          <select data-testid="ricorrenze-filtro-avvocato" value={avvFilter}
            onChange={(e) => setAvvFilter(e.target.value)}
            className="h-9 w-full border-2 border-slate-300 rounded-lg px-2 bg-white text-sm">
            <option value="">Tutti gli avvocati</option>
            {avvocati.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
          </select>
        </div>
      </div>

      {ricorrenze === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-indigo-800" /></div>
      ) : ricorrenze.length === 0 ? (
        <div data-testid="ricorrenze-empty" className="text-center py-12 text-slate-500">
          Nessuna ricorrenza emessa.
        </div>
      ) : (
        <div className="space-y-2">
          {ricorrenze.map((r) => (
            <div key={r.id} data-testid={`ricorrenza-row-${r.id}`}
              className="bg-white rounded-xl border-2 border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE[r.stato] || "bg-slate-100"}`}>{r.stato}</span>
                <span className="font-mono text-xs text-slate-600">{r.codice_documento}</span>
                <span className="text-sm">Avv. <b>{r.avvocato_nome}</b></span>
                <span className="text-xs text-slate-500">{r.pratiche?.length} pratica/e</span>
                <span className="ml-auto text-slate-500 text-xs">{fmtD(r.created_at)}</span>
              </div>
              <div className="text-sm text-slate-500">
                {(r.pratiche || []).slice(0, 3).map((p) => (
                  <span key={p.pratica_id} className="inline-block mr-3">
                    · {p.pratica_numero} ({p.pct_fee}% = {fmtEUR(p.fee_importo)})
                  </span>
                ))}
                {r.pratiche?.length > 3 && <span className="text-slate-400">...+{r.pratiche.length - 3}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-slate-900">Totale: {fmtEUR(r.imponibile_totale)}</span>
                <Button size="sm" variant="outline" data-testid={`ricorrenza-pdf-${r.id}`}
                  onClick={() => openSecureFile(`/credius/ricorrenze/${r.id}/pdf`)}
                  className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
                  <Download className="w-4 h-4 mr-1" /> PDF Fee
                </Button>
                {/* Iter123k175 · Nota Pro-forma (documento accompagnatorio) */}
                <Button size="sm" variant="outline" data-testid={`ricorrenza-proforma-${r.id}`}
                  onClick={() => openSecureFile(`/credius/ricorrenze/${r.id}/proforma-pdf`)}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50">
                  <Receipt className="w-4 h-4 mr-1" /> Nota P.F.
                </Button>
                {/* Iter123k175 · Invia all'avvocato con selezione documenti */}
                <select
                  data-testid={`ricorrenza-invia-select-${r.id}`}
                  disabled={busy}
                  defaultValue=""
                  onChange={(e) => {
                    const v = e.target.value; e.target.value = "";
                    if (!v) return;
                    inviaRicorrenza(r, v);
                  }}
                  className="h-9 border-2 border-brand text-brand bg-white rounded-md px-2 text-sm font-semibold hover:bg-brand/5 cursor-pointer">
                  <option value="">📤 Invia…</option>
                  <option value="fee">PDF Fee</option>
                  <option value="proforma">PDF Pro-forma</option>
                  <option value="entrambe">Entrambe</option>
                </select>
                {r.stato === "Emessa" && (
                  <Button size="sm" data-testid={`ricorrenza-pagata-${r.id}`}
                    onClick={() => confermaPagamento(r)} disabled={busy}
                    className="bg-emerald-700 hover:bg-emerald-800">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Conferma pagamento
                  </Button>
                )}
                {/* Iter122k51 · Cancella ricorrenza (soft-delete): utile per errori/test */}
                <Button size="sm" variant="outline" data-testid={`ricorrenza-elimina-${r.id}`}
                  onClick={async () => {
                    if (!window.confirm(`Eliminare la ricorrenza ${r.codice_documento}?\n\nTotale: ${fmtEUR(r.imponibile_totale)}\nStato: ${r.stato}\n\nLa cancellazione è logica (soft-delete): potrà essere recuperata dal DB in caso di errore.`)) return;
                    try {
                      await api.delete(`/credius/ricorrenze/${r.id}`);
                      toast.success("Ricorrenza eliminata");
                      load();
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
                  }}
                  className="border-red-300 text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-1" /> Elimina
                </Button>
                {r.stato === "Pagata" && (
                  <span className="text-xs text-slate-500 italic">Pagata il {fmtD(r.pagata_at)}</span>
                )}
                <Button size="sm" variant="outline" data-testid={`ricorrenza-elimina-${r.id}`}
                  onClick={() => {
                    if (!window.confirm(`Eliminare la ricorrenza ${r.codice_documento}?\n\nLa cancellazione è logica (soft-delete): potrà essere recuperata dal DB in caso di errore.`)) return;
                    api.delete(`/credius/ricorrenze/${r.id}`).then(load).catch((e) => alert(formatApiError(e)));
                  }}
                  title="Elimina ricorrenza (soft-delete)"
                  className="border-red-300 text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
